import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { EditIcon } from "@chakra-ui/icons";
import {
	Button,
	Flex,
	Modal,
	ModalOverlay,
	ModalHeader,
	ModalContent,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	Box,
	Grid,
	GridItem,
	Text,
	Divider,
	Card,
	CardHeader,
	CardBody,
	Tooltip,
	useDisclosure,
	Select,
	Wrap,
	WrapItem,
	useToast,
	RadioGroup,
	Radio,
	useColorModeValue,
	FormControl,
	FormLabel,
	Input,
	Stack,
	SimpleGrid,
	VStack,
	IconButton,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	Image,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiGlobe, FiHash, FiSettings, FiTrendingUp } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";
import { archivePropertyAsync, restorePropertyAsync, selectAllProperties, updateRentAsync } from "../../redux/user/userSlice";
import { API } from "../../utils/http";
import { getAdministrationStatus, getFinancialStatementStatus, getLeaseStatus } from "../../utils/utils";
import { editRentVerifier, getPropertyAddress } from "../../utils/verifier";
import ContactPopover from "../ContactPopover/ContactPopover";
import BuildingSuperPopover from "../ContactPopover/OwnerPopover";
import TenantPopover from "../ContactPopover/TenantPopover";
import StatusBadge from "../StatusBadge/StatusBadge";
import AdministrationStatusTooltipLabel from "../StatusTooltipLabel/AdministrationStatusTooltipLabel";
import LeaseStatusTooltipLabel from "../StatusTooltipLabel/LeaseStatusTooltipLabel";
import StatementStatusTooltipLabel from "../StatusTooltipLabel/StatementStatusTooltipLabel";

import AccountsTab from "./AccountsTab";
import ActivitiesTab from "./ActivitiesTab";
import Contacts from "./Contacts";
import DocumentsTab from "./DocumentsTab";
import FinancialStatementsTab from "./FinancialStatementsTab";
import GeneralnTab from "./General";
import ImagesTab from "./ImagesTab";
import MessagesTab from "./MessagesTab";
import NotificationsTab from "./NotificationsTab";
import PaymentsTab from "./PaymentsTab";
import SecurityDepositTab from "./SecurityDepositTab";
import SpecsTab from "./SpecsTab";
import TenantsTab from "./TenantsTab";
import TicketsTab from "./TicketsTab";
import UtilityTab from "./UtilityTab";

export default function Property() {
	const { id } = useParams();
	const property = useSelector(selectAllProperties)?.find((p) => p.id === id);
	const isArchived = property.archived;
	const navigate = useNavigate();
	const { isOpen: isConfirmRestoreModalOpen, onOpen: onConfirmRestoreModalOpen, onClose: onConfirmRestoreModalClose } = useDisclosure();
	const { t } = useTranslation();
	const status = useSelector((state) => state.user.status) === "loading";
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isArchiveOpen, onOpen: onArchiveOpen, onClose: onArchiveClose } = useDisclosure();
	const initialRentInfo = { ...property?.rent };
	const [rentInfo, setRentInfo] = useState(initialRentInfo);
	const dispatch = useDispatch();
	const [error, setError] = useState({});
	const toast = useToast();
	const [thumbnailImage, setThumbnailImage] = useState("");
	const location = useLocation();
	useEffect(() => {
		console.log("PROPERTY=============================================");
	}, []);

	const bgCard = useColorModeValue("white", "gray.700");
	const textColor = useColorModeValue("gray.700", "white");
	const borderColor = useColorModeValue("gray.200", "gray.600");
	const shadowColor = useColorModeValue("rgba(0, 0, 0, 0.1)", "rgba(0, 0, 0, 0.4)");
	const addressTextColor = useColorModeValue("gray.600", "gray.300");

	useEffect(() => {
		const thumbnail = property?.media?.filter((m) => m.contentType.includes("image"))[0];
		if (thumbnail) {
			const payload = { propertyId: property.id, media: [thumbnail] };
			API.put("get-media-urls", payload).then((r) => {
				console.log(r.data);
				setThumbnailImage(r.data.media[0]);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [property]);
	if (!property) {
		navigate(-1, { replace: true });
		return <>Redirecting...</>;
	}
	const confirmArchive = () => {
		console.log("archive property");
		dispatch(archivePropertyAsync({ propertyId: property.id })).then((r) => {
			if (r.error?.message)
				toast({
					title: "Error encountered",
					description: r.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			else {
				toast({
					title: "Success!",
					description: "Property archived!",
					status: "success",
					duration: 2000,
					isClosable: true,
				});
				navigate(`/properties`);
			}
		});
	};

	const queryParams = new URLSearchParams(location.search);
	const getDefaultTabIndex = () => {
		const tab = queryParams.get("tab");
		if (tab) {
			const index = tabs.findIndex((t) => t.id.toLowerCase() === tab.toLowerCase());
			if (index !== -1) return index;
		}
		return 0;
	};
	const tabs = [
		{
			id: "general",
			component: <GeneralnTab property={property} />,
		},
		{
			id: "specs",
			component: <SpecsTab property={property} />,
		},
		{
			id: "images",
			component: <ImagesTab property={property} />,
		},
		{
			id: "contacts",
			component: <Contacts property={property} loading={status} />,
		},
		{
			id: "utilities",
			component: <UtilityTab property={property} />,
		},
		{
			id: "payments",
			component: <PaymentsTab property={property} />,
		},
		{
			id: "securityDeposits",
			component: <SecurityDepositTab property={property} />,
		},
		{
			id: "financialStatements",
			component: <FinancialStatementsTab property={property} loading={status} />,
		},
		{
			id: "documents",
			component: <DocumentsTab property={property} />,
		},
		{
			id: "accounts",
			component: <AccountsTab property={property} />,
		},
		{
			id: "activities",
			component: <ActivitiesTab property={property} />,
		},
		{
			id: "tenants",
			component: <TenantsTab property={property} />,
		},
		{
			id: "notifications",
			component: <NotificationsTab property={property} />,
		},
		{
			id: "tickets",
			component: <TicketsTab property={property} />,
		},
		{
			id: "messages",
			component: <MessagesTab property={property} />,
		},
	];
	const getStatusText = (status) => {
		if (Object.values(status).includes("empty")) return "empty";
		if (Object.values(status).includes("incomplete")) return "incomplete";
		if (Object.values(status).includes("expired")) return "expired";
		if (Object.values(status).includes("warning")) return "warning";
		return "complete";
	};
	const saveRent = () => {
		const payload = { propertyId: property.id, rent: rentInfo };
		console.log("save rent", payload);
		const errors = editRentVerifier(rentInfo);
		console.log(errors);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		// return;
		dispatch(updateRentAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Update successful",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onClose();
				setRentInfo(initialRentInfo);
			});
	};
	const handleEditRentChange = (e) => {
		setRentInfo({ ...rentInfo, [e.target.name]: e.target.value });
	};

	const handleCloseEditRent = () => {
		onClose();
		setError({});
		setRentInfo(initialRentInfo);
	};

	const renderEditRent = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseEditRent}
			size="4xl"
			icon={FiTrendingUp}
			title={t("property.editRent")}
			subtitle={t("property.editRentSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseEditRent} onConfirm={saveRent} confirmLabel={t("misc.save")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiTrendingUp} title={t("property.financialInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl isInvalid={error?.amount}>
									<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
									<Input
										size="sm"
										type="number"
										name="amount"
										onChange={handleEditRentChange}
										value={rentInfo.amount}
										placeholder={t("property.enterAmount")}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl isInvalid={error?.currency}>
									<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
									<Select
										size="sm"
										name="currency"
										value={rentInfo.currency}
										onChange={handleEditRentChange}
										placeholder={t("property.selectCurrency")}
										borderRadius="md"
									>
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
								</FormControl>
							</SimpleGrid>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl isInvalid={error?.firstRentDay}>
									<ModalFieldLabel icon={FiCalendar}>{t("property.firstRentDay")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleEditRentChange}
										type="number"
										value={rentInfo.firstRentDay}
										name="firstRentDay"
										placeholder={t("property.firstDay")}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl isInvalid={error?.lastRentDay}>
									<ModalFieldLabel icon={FiCalendar}>{t("property.lastRentDay")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleEditRentChange}
										type="number"
										name="lastRentDay"
										value={rentInfo.lastRentDay}
										placeholder={t("property.lastDay")}
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
						</VStack>
					</PropertyModalSection>
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiGlobe} title={t("property.exchangeRateConfiguration")}>
						<VStack spacing={3} align="stretch">
							<FormControl>
								<ModalFieldLabel icon={FiGlobe}>{t("property.rentExchangeRate")}</ModalFieldLabel>
								<RadioGroup
									name="exchangeRateType"
									onChange={(e) => {
										if (e === "fixedExchangeRate") setRentInfo({ ...rentInfo, exchangeRateType: e, fixedExchangeRate: 4.9 });
										else {
											const tempRent = { ...rentInfo };
											delete tempRent.fixedExchangeRate;
											setRentInfo({ ...tempRent, exchangeRateType: e });
										}
									}}
									value={rentInfo.exchangeRateType}
								>
									<VStack spacing={2} align="start">
										<Radio size="sm" value="bnr">
											BNR din ziua platii
										</Radio>
										<Radio size="sm" value="bnr2">
											BNR+2% din ziua platii
										</Radio>
										<Radio size="sm" value="fixedExchangeRate">
											Curs fix
										</Radio>
									</VStack>
								</RadioGroup>
							</FormControl>
							<FormControl hidden={rentInfo.exchangeRateType !== "fixedExchangeRate"} isInvalid={error?.fixedExchangeRate}>
								<ModalFieldLabel icon={FiGlobe}>{t("property.fixedExchangeRate")}</ModalFieldLabel>
								<Input
									size="sm"
									isDisabled={rentInfo.exchangeRateType !== "fixedExchangeRate"}
									maxW={24}
									type="number"
									name="fixedExchangeRate"
									onChange={handleEditRentChange}
									value={rentInfo.fixedExchangeRate}
									placeholder={t("property.enterExchangeRate")}
									borderRadius="md"
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiSettings} title={t("property.administrationFeeOptional")}>
						<SimpleGrid columns={2} spacing={4} w="full">
							<FormControl isInvalid={error?.fixedAdministrationFeeAmount}>
								<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
								<Input
									size="sm"
									name="fixedAdministrationFeeAmount"
									placeholder={t("property.enterAmount")}
									value={rentInfo.fixedAdministrationFeeAmount}
									onChange={handleEditRentChange}
									type="number"
									borderRadius="md"
								/>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
								<Select
									size="sm"
									name="fixedAdministrationFeeCurrency"
									onChange={handleEditRentChange}
									value={rentInfo.fixedAdministrationFeeCurrency}
									placeholder={t("property.selectCurrency")}
									borderRadius="md"
								>
									<option value="eur">EUR</option>
									<option value="ron">RON</option>
								</Select>
							</FormControl>
						</SimpleGrid>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);
	const renderPersistentInfo = (thumbnailImageUrl) => {
		const administrationStatus = getAdministrationStatus(property);
		const leaseStatus = getLeaseStatus(property);
		const financialStatementStatus = getFinancialStatementStatus(property);

		return (
			<Box>
				<Stack spacing={3}>
					<Box>
						<Image
							fallbackSrc="https://fakeimg.pl/250x250?text=RentIT&font=noto"
							src={thumbnailImageUrl}
							borderRadius="lg"
							objectFit="contain"
							width="100%"
							height="120px"
						/>
					</Box>

					<Box>
						{/* <Text fontWeight="bold" fontSize="sm" mb={2} color={textColor}>
							Status
						</Text> */}
						<Stack spacing={2}>
							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.administration")}:
								</Text>
								<StatusBadge
									status={getStatusText(administrationStatus)}
									tooltipLabel={<AdministrationStatusTooltipLabel property={property} administrationStatus={administrationStatus} />}
								/>
							</Flex>
							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.lease")}:
								</Text>
								<StatusBadge status={getStatusText(leaseStatus)} tooltipLabel={<LeaseStatusTooltipLabel property={property} leaseStatus={leaseStatus} />} />
							</Flex>
							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.financial")}:
								</Text>
								<StatusBadge
									status={getStatusText(financialStatementStatus)}
									tooltipLabel={<StatementStatusTooltipLabel statementStatus={financialStatementStatus} />}
								/>
							</Flex>
						</Stack>
					</Box>

					<Divider />

					<Box>
						<Stack spacing={2}>
							<Flex align="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.rentPrice")}:
								</Text>
								<Flex align="center">
									<Text fontSize="xs" color={textColor}>
										{property.rent.amount ? `${property.rent.amount} ${property.rent.currency}` : t("property.notAvailable")}
									</Text>
									<Tooltip label={t("property.editRent")}>
										<IconButton
											isDisabled={isArchived}
											marginLeft={1}
											icon={<EditIcon />}
											colorScheme="blue"
											variant="ghost"
											size="xs"
											onClick={() => {
												onOpen();
												setRentInfo({ ...rentInfo, ...property.rent });
											}}
										/>
									</Tooltip>
								</Flex>
							</Flex>

							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.rentCollection")}:
								</Text>
								{/* <Text color={addressTextColor} fontSize="xs">
									{property.rent.firstRentDay && property.rent.lastRentDay
										? moment().format(`${property.rent.firstRentDay} MMM YYYY`) +
										  " - " +
										  (property.rent.lastRentDay > moment().daysInMonth()
												? moment().endOf("month").format("DD MMM YYYY")
												: moment().format(`${property.rent.lastRentDay} MMM YYYY`))
										: t("property.notAvailable")}
								</Text> */}
								<Text color={addressTextColor} fontSize="xs">
									{property.rent.firstRentDay && property.rent.lastRentDay
										? `${moment().format(`${property.rent.firstRentDay}`)} - ${moment().format(`${property.rent.lastRentDay}`)} ${moment().format("MMM YYYY")}`
										: t("property.notAvailable")}
								</Text>
							</Flex>

							{property.rent.fixedAdministrationFeeAmount && (
								<Flex alignItems="center">
									<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
										{t("property.fixedAdministrationFee")}:
									</Text>
									<Text color={addressTextColor} fontSize="xs">
										{`${property.rent.fixedAdministrationFeeAmount} ${property.rent.fixedAdministrationFeeCurrency}`}
									</Text>
								</Flex>
							)}

							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.rentExchangeRate")}:
								</Text>
								<Text color={addressTextColor} fontSize="xs">
									{property.rent.exchangeRateType === "fixedExchangeRate"
										? `Curs fix 1 EUR = ${property.rent.fixedExchangeRate} RON`
										: property.rent.exchangeRateType === "bnr2"
										? `BNR+2% din ziua platii`
										: property.rent.exchangeRateType === "bnr"
										? `BNR din ziua platii`
										: t("property.notAvailable")}
								</Text>
							</Flex>
						</Stack>
					</Box>

					<Divider />

					<Box>
						<Stack spacing={2}>
							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.agent")}:
								</Text>
								<Text color={addressTextColor} fontSize="xs">
									{property.agent}
								</Text>
							</Flex>

							<Flex alignItems="center">
								<Text fontSize="xs" fontWeight="medium" color={textColor} mr={1}>
									{t("property.dateAdded")}:
								</Text>
								<Text color={addressTextColor} fontSize="xs">
									{moment(property.addedDate).format("DD MMM YYYY")}
								</Text>
							</Flex>

							<Box>
								<Text fontSize="xs" fontWeight="medium" color={textColor} mb={2}>
									{t("property.contacts")}:
								</Text>
								<Wrap spacing={1}>
									{property.owner.email && (
										<WrapItem>
											<BuildingSuperPopover contact={property.owner} />
										</WrapItem>
									)}
									{property.tenants
										?.filter((t) => !t.former)
										.map((t, index) => (
											<WrapItem key={index}>
												<TenantPopover contact={t} propertyId={property.id} />
											</WrapItem>
										))}
									{property.buildingSuper.email && (
										<WrapItem>
											<BuildingSuperPopover contact={property.buildingSuper} />
										</WrapItem>
									)}
									{property.contacts?.map((c, index) => (
										<WrapItem key={index}>
											<ContactPopover contact={c} property={property} />
										</WrapItem>
									))}
								</Wrap>
							</Box>
						</Stack>
					</Box>
				</Stack>
			</Box>
		);
	};
	const renderArchiveProperty = () => {
		return (
			<Modal isOpen={isArchiveOpen} onClose={onArchiveClose} size={"xl"}>
				<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
				<ModalContent>
					<ModalHeader>
						<Text>Archive property</Text>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Stack>
							<Text>Are you sure you want to archive this property?</Text>
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Stack direction="row">
							<Button colorScheme="red" onClick={confirmArchive}>
								Archive
							</Button>
							<Button colorScheme="blue" onClick={onArchiveClose} mr={3}>
								{t("misc.cancel")}
							</Button>
						</Stack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};
	const handleRestoreProperty = () => {
		dispatch(restorePropertyAsync({ propertyId: property.id }))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Property restored!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onConfirmRestoreModalClose();
			});
	};

	const renderConfirmRestoreModal = () => {
		return (
			<Modal isOpen={isConfirmRestoreModalOpen} onClose={onConfirmRestoreModalClose}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Restore Property</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text>Are you sure you want to restore this property?</Text>
					</ModalBody>

					<ModalFooter>
						<Button colorScheme="blue" mr={3} onClick={onConfirmRestoreModalClose}>
							Close
						</Button>
						<Button colorScheme="blue" variant="outline" size="sm" onClick={handleRestoreProperty}>
							Restore
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};
	return (
		<Card bg={bgCard} boxShadow={`0 4px 6px ${shadowColor}`} borderRadius="lg">
			<CardHeader borderBottom="1px" borderColor={borderColor} py={3}>
				<Flex direction={"row"} alignItems="center" justifyContent={"space-between"}>
					<Box>
						<Text fontSize="lg" fontWeight="bold" color={textColor}>
							Property Details
						</Text>
						<Text color={addressTextColor} fontSize="sm">
							{getPropertyAddress(property)}
						</Text>
					</Box>
					<Box>
						{property.archived ? (
							<Button colorScheme="blue" variant="outline" size="sm" onClick={onConfirmRestoreModalOpen}>
								{t("property.restore")}
							</Button>
						) : (
							<Button colorScheme="red" onClick={onArchiveOpen} size="sm">
								{t("property.archive")}
							</Button>
						)}
						{renderConfirmRestoreModal()}
						{renderArchiveProperty()}
					</Box>
				</Flex>
			</CardHeader>
			<CardBody pt={3}>
				<Tabs variant="soft-rounded" defaultIndex={getDefaultTabIndex()} isLazy colorScheme="blue" size="sm">
					<TabList overflowX="auto" overflowY="hidden" pb={2} mb={2} borderBottom="1px" borderColor={borderColor}>
						{tabs.map((tab, index) => (
							<Tab key={index} _selected={{ color: "blue.500", bg: "blue.50" }} mx={0.5} py={1} px={3} fontSize="sm">
								{t(`property.${tab.id}`)}
							</Tab>
						))}
					</TabList>
					<TabPanels>
						{tabs.map((tab, index) => (
							<TabPanel key={index} px={0} py={2}>
								<Grid templateColumns="repeat(6, 1fr)" gap={3}>
									<GridItem colSpan={{ base: "6", lg: "1" }}>{renderPersistentInfo(thumbnailImage?.url)}</GridItem>
									<GridItem colSpan={{ base: "6", lg: "5" }}>
										<Card variant="elevated" bg={bgCard} borderRadius="lg" boxShadow={`0 2px 4px ${shadowColor}`}>
											<CardHeader borderBottom="1px" borderColor={borderColor} p={3}>
												<Text fontSize="md" fontWeight="bold" color={textColor}>
													{t(`property.${tab.id}`)}
												</Text>
											</CardHeader>
											<CardBody p={3}>{tab.component}</CardBody>
										</Card>
									</GridItem>
								</Grid>
							</TabPanel>
						))}
					</TabPanels>
				</Tabs>
				{renderEditRent()}
			</CardBody>
		</Card>
	);
}
